import React, { useState, useEffect } from "react";
import { getAuth, signOut, onAuthStateChanged } from "firebase/auth";
import { useNavigate, useLocation } from "react-router-dom";
import EditarPerfil from "./EditarPerfil";
import MascotasPublicadas from "../pets/MascotasPublicadas";
import SignIn from "./SignIn";
import { getSingleUserData, updateSingleDoc } from "../../utils";
import Notification from "../Notification";
import Donaciones from "./Donaciones";
import ToolTips from "../ToolTips";

const Perfil = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const auth = getAuth();

  const [userData, setUserData] = useState(null);
  const [editingProfile, setEditingProfile] = useState(false);
  const [selectedSection, setSelectedSection] = useState(
    () => location.state?.section ?? "Mi perfil"
  );
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState(null);
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);
  const [signInKey, setSignInKey] = useState(0);

  useEffect(() => {
    if (location.state?.section) {
      setSelectedSection(location.state.section);
    }
  }, [location.state?.section]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setIsUserLoggedIn(true);
        setSelectedSection((prev) =>
          prev === "Iniciar Sesión" ? "Mi perfil" : prev
        );

        try {
          const uData = await getSingleUserData(user);
          setUserData({
            docId: uData[0].id,
            uid: user.uid,
            email: user.email,
            username: user.displayName,
            phoneNumber: uData[0].phone,
          });
        } catch (error) {
          console.error("Error loading user data:", error);
        }
      } else {
        setIsUserLoggedIn(false);
        setUserData(null);
        setSelectedSection(
          location.state?.section ? location.state.section : "Iniciar Sesión"
        );
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [auth, location.state?.section]);

  const handleSaveProfile = async (updatedData) => {
    try {
      await updateSingleDoc(userData.docId, updatedData.phoneNumber);
      setNotification({
        type: "success",
        message: "Se actualizó correctamente.",
      });
      setUserData(updatedData);
      setEditingProfile(false);
    } catch (error) {
      console.error("Error updating profile:", error);
      setNotification({
        type: "error",
        message: "Hubo un error al actualizar. Por favor inténtalo de nuevo.",
      });
    }
  };

  const closeNotification = () => setNotification(null);

  const sections = isUserLoggedIn
    ? [
        {
          name: "Mi perfil",
          description: "Visualiza y edita tu información personal.",
        },
        {
          name: "Mascotas Publicadas",
          description: "Administra tus publicaciones.",
        },
        {
          name: "Donaciones",
          description: "Apoya nuestro trabajo.",
        },
      ]
    : [
        {
          name: "Iniciar Sesión",
          description: "Accede a tu cuenta para gestionar tus publicaciones.",
        },
        {
          name: "Donaciones",
          description: "Apoya nuestro trabajo.",
        },
      ];

  const renderSection = () => {
    if (!isUserLoggedIn && selectedSection === "Iniciar Sesión") {
      return <SignIn key={signInKey} />;
    }

    if (isUserLoggedIn && selectedSection === "Mi perfil") {
      return editingProfile ? (
        <EditarPerfil
          userData={userData}
          onSave={handleSaveProfile}
          onCancel={() => setEditingProfile(false)}
        />
      ) : (
        <div className="space-y-6 z-10 bg-white">
          <div className="flex flex-col items-center">
            <img
              className="w-32 h-32 object-cover rounded-full shadow-xl"
              src={auth.currentUser?.photoURL || ""}
              alt="Profile"
            />
          </div>
          <div className="grid grid-cols-1 gap-8">
            {[
              {
                label: "Correo electrónico",
                name: "email",
                value: userData?.email,
                disabled: true,
              },
              {
                label: "Nombre de usuario",
                name: "username",
                value: userData?.username || "",
                disabled: true,
              },
              {
                label: "Número de teléfono",
                name: "phoneNumber",
                value: userData?.phoneNumber || "",
                disabled: true,
              },
            ].map((field, idx) => (
              <div
                key={idx}
                className="flex font-main flex-col items-center justify-center"
              >
                <span className="text-xl font-main text-gray-600">
                  {field.label}
                </span>

                {field.label === "Número de teléfono" ? (
                  <ToolTips
                    text="Este es el numero con el que se contactaràn los demàs usuarios"
                    position="top"
                  >
                    <input
                      type="text"
                      name={field.name}
                      value={field.value}
                      disabled={field.disabled}
                      className="font-main font-medium h-full w-1/2 p-2 pl-4 pr-8 appearance-none bg-white border-2 border-gray-200 rounded-lg hover:border-gray-400 focus:ring-1 focus:ring-[#504527] transition-all duration-200 shadow-sm sm:w-[70%]"
                    />
                  </ToolTips>
                ) : (
                  <input
                    type="text"
                    name={field.name}
                    value={field.value}
                    disabled={field.disabled}
                    className="font-main font-medium h-full w-1/2 p-2 pl-4 pr-8 appearance-none bg-white border-2 border-gray-200 rounded-lg hover:border-gray-400 focus:ring-1 focus:ring-[#504527] transition-all duration-200 shadow-sm sm:w-[70%]"
                  />
                )}
              </div>
            ))}

            <div className="flex flex-col items-center justify-center">
              <button
                onClick={() => setEditingProfile(true)}
                className="font-main font-medium text-xl px-4 py-2 w-[10rem] bg-secondary text-white rounded hover:bg-orange-700 transition"
              >
                Editar perfil
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (isUserLoggedIn && selectedSection === "Mascotas Publicadas") {
      return <MascotasPublicadas />;
    }

    if (selectedSection === "Donaciones") {
      return <Donaciones />;
    }

    return null;
  };

  return (
    <section className="flex flex-row justify-center py-32 sm:py-4 px-[15dvw] sm:px-0 items-start w-full min-h-screen bg-orange-50 sm:flex-col-reverse sm:gap-0 sm:items-center">
      <div className="min-w-[25rem] sm:min-w-full h-[38rem] sm:h-fit sm:pt-6 flex flex-col justify-center items-center rounded sm:rounded-none z-10 bg-white ">
        {sections.map((section) => (
  <button
    key={section.name}
    onClick={() => {
      setSelectedSection(section.name);
      window.scrollTo({ top: 0, behavior: "smooth" }); // <-- scroll al top
      if (section.name === "Iniciar Sesión") {
        setSignInKey((prev) => prev + 1);
      }
    }}
    className={`w-[22rem] h-[7rem] flex flex-row items-center justify-between py-4 px-6 rounded text-sm  font-main transform transition-all duration-300 ${
      selectedSection === section.name
        ? "bg-gray-100 scale-105"
        : "bg-transparent scale-100"
    }`}
  >
    <div className="flex flex-col items-start">
      <span className="text-2xl font-main font-medium">{section.name}</span>
      <span className="text-base font-main text-gray-500 font-regular mt-1 text-left">
        {section.description}
      </span>
    </div>
    <span className="text-lg text-gray-800">{">"}</span>
  </button>
))}

        <div className="mt-10 sm:mt-4 w-full flex flex-col items-center text-center">
          {isUserLoggedIn && (
            <button
              onClick={() => signOut(auth).then(() => navigate("/"))}
              className="font-main font-medium text-xl w-[22rem] py-4 bg-red-600 hover:bg-red-700 text-white rounded shadow-lg"
            >
              Cerrar sesión
            </button>
          )}
        </div>
      </div>

      <div className="w-[60rem] min-w-[450px] min-h-[38rem] sm:min-h-fit ml-5 sm:ml-0 flex flex-col justify-start rounded z-10 bg-white sm:pt-8 p-9 sm:w-full">
        <div>{loading ? <p>Cargando...</p> : <>{renderSection()}</>}</div>
      </div>

      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          closeNotification={closeNotification}
        />
      )}
      <div className="w-screen absolute -top-[5px] left-0 overflow-hidden">
        <svg
          width="270%"
          height="100%"
          id="svg"
          viewBox="0 0 1440 490"
          xmlns="http://www.w3.org/2000/svg"
          className="transition duration-300 ease-in-out delay-150 w-[270%] md:w-[100%]">
        
          <path
            d="M 0,500 L 0,93 C 56.27435897435893,109.58461538461539 112.54871794871787,126.16923076923078 203,127 C 293.45128205128213,127.83076923076922 418.0794871794873,112.9076923076923 500,99 C 581.9205128205127,85.0923076923077 621.1333333333333,72.2 684,63 C 746.8666666666667,53.8 833.3871794871797,48.2923076923077 927,58 C 1020.6128205128203,67.7076923076923 1121.3179487179486,92.63076923076922 1208,101 C 1294.6820512820514,109.36923076923078 1367.3410256410257,101.1846153846154 1440,93 L 1440,500 L 0,500 Z"
            stroke="none"
            strokeWidth="0"
            fill="#dc5d00"
            fillOpacity="0.4"
            className="transition-all duration-300 ease-in-out delay-150 path-0"
            transform="rotate(-180 720 250)"
          ></path>
          <path
            d="M 0,500 L 0,218 C 70.46923076923076,227.3230769230769 140.93846153846152,236.64615384615382 226,230 C 311.0615384615385,223.35384615384618 410.71538461538466,200.73846153846156 488,203 C 565.2846153846153,205.26153846153844 620.1999999999999,232.4 694,228 C 767.8000000000001,223.6 860.4846153846156,187.66153846153847 954,181 C 1047.5153846153844,174.33846153846153 1141.8615384615382,196.95384615384614 1223,208 C 1304.1384615384618,219.04615384615386 1372.069230769231,218.52307692307693 1440,218 L 1440,500 L 0,500 Z"
            stroke="none"
            strokeWidth="0"
            fill="#dc5d00"
            fillOpacity="0.53"
            className="transition-all duration-300 ease-in-out delay-150 path-1"
            transform="rotate(-180 720 250)"
          ></path>
          <path
            d="M 0,500 L 0,343 C 73.82820512820513,328.35128205128206 147.65641025641025,313.70256410256417 218,327 C 288.34358974358975,340.29743589743583 355.2025641025641,381.5410256410256 446,388 C 536.7974358974359,394.4589743589744 651.5333333333332,366.1333333333334 748,360 C 844.4666666666668,353.8666666666666 922.6641025641027,369.92564102564097 1001,363 C 1079.3358974358973,356.07435897435903 1157.8102564102564,326.1641025641026 1231,319 C 1304.1897435897436,311.8358974358974 1372.0948717948718,327.41794871794866 1440,343 L 1440,500 L 0,500 Z"
            stroke="none"
            strokeWidth="0"
            fill="#dc5d00"
            fillOpacity="1"
            className="transition-all duration-300 ease-in-out delay-150 path-2"
            transform="rotate(-180 720 250)"
          ></path>
        </svg>
      </div>
    </section>
  );
};

export default Perfil;
